import React from "react";
import { AdaAmount } from "@/components/tx/AdaAmount";
import { UtxoRow } from "@/components/tx/UtxoRow";
import { _sumLovelace } from "@/components/tx/txHelpers";

// ---------- From/To address lists (tx_utxos tab) ----------
// NOTE: totalIn/totalOut/fees are derived here (previously this component
// referenced them without defining them, which threw a ReferenceError as
// soon as a user opened the "UTXOs" tab on a transaction — fixed during the
// App.js modularization).
export const FromToList = ({ inputs, outputs }) => {
  const totalIn = _sumLovelace(inputs);
  const totalOut = _sumLovelace(outputs);
  const fees = totalIn > totalOut ? totalIn - totalOut : 0n;
  return (
    <div className="space-y-8">
      {/* From Addresses (Inputs) */}
      <section>
        <h3 className="font-heading text-[16px] md:text-[18px] text-primary text-glow mb-3">
          From Addresses (Inputs)
        </h3>
        <div className="relative overflow-hidden rounded-2xl border border-primary/20 bg-card/30 backdrop-blur-md">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/60 to-transparent opacity-50 pointer-events-none" />
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 px-5 py-3 border-b border-primary/15 text-[10px] uppercase tracking-[0.25em] text-muted-foreground mono">
            <span>Address</span>
            <span className="md:text-right">Amount</span>
          </div>
          {(inputs || []).map((r, i) => (
            <UtxoRow key={`in-${r.tx_hash}-${r.output_index}-${i}`} row={r} side="in" />
          ))}
          {(!inputs || inputs.length === 0) ? (
            <div className="px-5 py-6 text-[12px] mono text-muted-foreground/60">no inputs</div>
          ) : null}
          <div className="flex items-center justify-between gap-4 px-5 py-3 border-t border-primary/15 bg-primary/[0.03]">
            <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mono">
              Total Input
            </span>
            <AdaAmount lovelaceStr={totalIn.toString()} />
          </div>
        </div>
      </section>

      {/* To Addresses (Outputs) */}
      <section>
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <h3 className="font-heading text-[16px] md:text-[18px] text-primary text-glow">
            To Addresses (Outputs)
          </h3>
          {fees > 0n ? (
            <span
              data-testid="utxo-fees-badge"
              className="inline-flex items-center gap-2 px-3 py-1 rounded-md border border-rose-400/30 bg-rose-400/[0.04] mono text-[11px] uppercase tracking-[0.2em]"
            >
              <span className="text-muted-foreground">Fees</span>
              <AdaAmount lovelaceStr={fees.toString()} tone="danger" />
            </span>
          ) : null}
        </div>
        <div className="relative overflow-hidden rounded-2xl border border-primary/20 bg-card/30 backdrop-blur-md">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/60 to-transparent opacity-50 pointer-events-none" />
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 px-5 py-3 border-b border-primary/15 text-[10px] uppercase tracking-[0.25em] text-muted-foreground mono">
            <span>Address</span>
            <span className="md:text-right">Amount</span>
          </div>
          {(outputs || []).map((r, i) => (
            <UtxoRow key={`out-${r.address}-${r.output_index}-${i}`} row={r} side="out" />
          ))}
          {(!outputs || outputs.length === 0) ? (
            <div className="px-5 py-6 text-[12px] mono text-muted-foreground/60">no outputs</div>
          ) : null}
          <div className="flex items-center justify-between gap-4 px-5 py-3 border-t border-primary/15 bg-primary/[0.03]">
            <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mono">
              Total Output
            </span>
            <AdaAmount lovelaceStr={totalOut.toString()} />
          </div>
        </div>
      </section>
    </div>
  );
};
